import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, Receipt,
  AlertTriangle, ShieldAlert, Clock, FolderKanban, Users,
  PackageX, ArrowRight, BarChart3, Minus, RefreshCw, Calculator,
  ClipboardCheck, ShieldCheck,
} from 'lucide-react';
import { KpiCard } from './KpiCard';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { OCSinFacturaAlertWidget } from '../../components/dashboard/OCSinFacturaAlertWidget';
import { BandejaAprobacionesWidget } from '../../components/dashboard/BandejaAprobacionesWidget';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(v || 0);

const pct = (curr, prev) => {
  if (!prev) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

const PctBadge = ({ curr, prev }) => {
  const p = pct(curr, prev);
  if (p === null) return null;
  const isUp = p >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${isUp ? 'text-emerald-600' : 'text-red-600'}`}>
      {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(p).toFixed(1)}%
    </span>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 text-xs space-y-1">
      <p className="font-semibold text-gray-700">{label}</p>
      {payload.map((p, i) => (
        <p key={`tooltip-${p.name}`} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
      ))}
    </div>
  );
};

export function DashboardGerencia({ data, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshRemun = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetch(`${API}/api/dashboard/gerencia/refresh-remuneraciones`, {
        method: 'POST',
        credentials: 'include',
      });
      if (onRefresh) onRefresh();
    } catch { }
    finally { setRefreshing(false); }
  }, [onRefresh]);

  if (!data) return null;

  const d = data;
  const dm = d.desglose_margen || {};

  return (
    <div className="space-y-6" data-testid="dashboard-gerencia">

      {/* ══════ BANDEJA DE APROBACIONES (1er lugar — acción inmediata) ══════ */}
      <BandejaAprobacionesWidget />

      {/* ══════ SECCION 1: MES ACTUAL (informativo, sin margen) ══════ */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          {d.mes_actual} — En curso
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="md:col-span-2 border-l-4 border-l-blue-600 bg-gradient-to-br from-blue-50/60 to-white">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-medium uppercase text-muted-foreground">Ventas Netas Acumuladas {d.anio}</span>
              </div>
              <p className="text-3xl font-bold text-blue-700" data-testid="ventas-acumulado">{fmt(d.ventas_acumulado_anio)}</p>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="text-muted-foreground">{d.mes_actual}: <b className="text-gray-800">{fmt(d.ventas_mes_actual)}</b></span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-orange-500" />
                <span className="text-xs font-medium uppercase text-muted-foreground">Compras/Gastos a la fecha</span>
              </div>
              <p className="text-2xl font-bold text-orange-700" data-testid="compras-actual">{fmt(d.compras_mes_actual)}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-rose-400">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-rose-400" />
                <span className="text-xs font-medium uppercase text-muted-foreground">Finiquitos {d.mes_actual}</span>
              </div>
              <p className="text-2xl font-bold text-rose-600" data-testid="finiquitos-actual">{fmt(d.finiquitos_mes_actual)}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ══════ SECCION 2: MARGEN CERRADO MES ANTERIOR ══════ */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Calculator className="w-4 h-4" />
          Margen {d.mes_anterior} — Cerrado
          <button
            onClick={handleRefreshRemun}
            disabled={refreshing}
            className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors"
            data-testid="refresh-remun-btn"
            title="Actualizar datos desde Alegra"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Card principal: Margen cerrado */}
          <Card className={`lg:col-span-1 border-l-4 ${(d.margen_cerrado || 0) >= 0 ? 'border-l-teal-600 bg-gradient-to-br from-teal-50/50 to-white' : 'border-l-red-500 bg-gradient-to-br from-red-50/50 to-white'}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                {(d.margen_cerrado || 0) >= 0
                  ? <TrendingUp className="w-5 h-5 text-teal-600" />
                  : <TrendingDown className="w-5 h-5 text-red-500" />}
                <span className="text-xs font-medium uppercase text-muted-foreground">Margen {d.mes_anterior}</span>
              </div>
              <p className={`text-3xl font-bold ${(d.margen_cerrado || 0) >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-testid="margen-cerrado">
                {fmt(d.margen_cerrado)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Ventas - Compras - Sueldos - Cotizaciones - Finiquitos</p>
            </CardContent>
          </Card>

          {/* Desglose completo del margen */}
          <Card className="lg:col-span-2">
            <CardContent className="p-5">
              <p className="text-xs font-medium uppercase text-muted-foreground mb-3">Desglose Margen {d.mes_anterior}</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-blue-700 font-medium">Ventas netas</span>
                  <span className="font-bold text-blue-700">{fmt(dm.ventas)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-orange-600">(-) Compras / Gastos</span>
                  <span className="font-semibold text-orange-600">-{fmt(dm.compras)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-violet-600">(-) Sueldos <span className="text-xs text-muted-foreground">(pagados en {d.mes_actual})</span></span>
                  <span className="font-semibold text-violet-600">-{fmt(dm.sueldos)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-violet-600">(-) Cotizaciones <span className="text-xs text-muted-foreground">(pagadas en {d.mes_actual})</span></span>
                  <span className="font-semibold text-violet-600">-{fmt(dm.cotizaciones)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-rose-500">(-) Finiquitos {d.mes_anterior}</span>
                  <span className="font-semibold text-rose-500">-{fmt(dm.finiquitos)}</span>
                </div>
                <div className="border-t-2 border-dashed pt-2 flex justify-between text-sm">
                  <span className="font-bold text-gray-800">= MARGEN</span>
                  <span className={`font-bold text-lg ${(dm.margen || 0) >= 0 ? 'text-teal-700' : 'text-red-700'}`}>
                    {fmt(dm.margen)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Row 3: CxC, CxP, Liquidez ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard title="Cuentas por Cobrar" value={fmt(d.cuentas_por_cobrar)} icon={Wallet} color="text-green-600" bgColor="bg-green-50" />
        <KpiCard title="Cuentas por Pagar" value={fmt(d.cuentas_por_pagar)} icon={Wallet} color="text-red-600" bgColor="bg-red-50" />
        <KpiCard
          title="Liquidez Neta"
          value={fmt(d.liquidez_neta)}
          icon={d.liquidez_neta >= 0 ? TrendingUp : TrendingDown}
          color={d.liquidez_neta >= 0 ? 'text-emerald-600' : 'text-red-600'}
          bgColor={d.liquidez_neta >= 0 ? 'bg-emerald-50' : 'bg-red-50'}
        />
      </div>

      {/* ── Row 4: Tendencia 6M chart + Alertas ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Tendencia Ventas vs Compras (6 meses)
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64" data-testid="chart-tendencia">
            {d.tendencia_6m?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={d.tendencia_6m} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v / 1e6).toFixed(0)}M`} tick={{ fontSize: 11 }} width={55} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="ventas" name="Ventas" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={28} />
                  <Bar dataKey="compras" name="Compras" fill="#f97316" radius={[4, 4, 0, 0]} barSize={28} />
                  <Line dataKey="margen" name="Margen" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Sin datos de tendencia</div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3">
          <Card className={d.urgentes_sii > 0 ? 'ring-2 ring-red-400' : ''}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${d.urgentes_sii > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                  <ShieldAlert className={`w-5 h-5 ${d.urgentes_sii > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Urgentes SII</p>
                  <p className={`text-xl font-bold ${d.urgentes_sii > 0 ? 'text-red-600' : 'text-gray-700'}`}>{d.urgentes_sii}</p>
                </div>
              </div>
              <Link to="/contabilidad/acepta-rechazo-dte" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5">
                Ver <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Receipt className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Lotes Pendientes VB</p>
                  <p className="text-xl font-bold text-gray-700">{d.lotes_pendientes_vb}</p>
                </div>
              </div>
              <Link to="/compras/firma-vb-lote" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5">
                Ver <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>

          <Card className={d.requisiciones_pendientes > 0 ? 'ring-2 ring-indigo-400' : ''}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${d.requisiciones_pendientes > 0 ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                  <ClipboardCheck className={`w-5 h-5 ${d.requisiciones_pendientes > 0 ? 'text-indigo-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Requisiciones Pend.</p>
                  <p className={`text-xl font-bold ${d.requisiciones_pendientes > 0 ? 'text-indigo-600' : 'text-gray-700'}`}>{d.requisiciones_pendientes || 0}</p>
                </div>
              </div>
              <Link to="/compras/requisiciones" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5" data-testid="link-requisiciones-pendientes">
                Aprobar <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Rechazos SII Pend.</p>
                  <p className="text-xl font-bold text-gray-700">{d.rechazos_sii_pendientes}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={d.ocs_sin_vb > 0 ? 'ring-2 ring-green-400' : ''}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${d.ocs_sin_vb > 0 ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <ShieldCheck className={`w-5 h-5 ${d.ocs_sin_vb > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">OCs Sin VB</p>
                  <p className={`text-xl font-bold ${d.ocs_sin_vb > 0 ? 'text-green-600' : 'text-gray-700'}`}>{d.ocs_sin_vb || 0}</p>
                </div>
              </div>
              <Link to="/compras/ordenes-compra" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5" data-testid="link-ocs-sin-vb">
                Dar VB <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-2">
            <KpiCard title="OTs Activas" value={d.ots_ejecucion} icon={Clock} color="text-amber-600" bgColor="bg-amber-50" />
            <KpiCard title="Proyectos" value={d.proyectos_vigentes} icon={FolderKanban} color="text-blue-600" bgColor="bg-blue-50" />
            <KpiCard title="HH Mes" value={d.hh_mes} icon={Users} color="text-violet-600" bgColor="bg-violet-50" subtitle={`${d.hh_trabajadores} trab.`} />
          </div>
        </div>
      </div>

      {/* ── Row 5: Top clientes / Top proveedores ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Top 5 Clientes ({d.mes_actual})</CardTitle>
          </CardHeader>
          <CardContent>
            {d.top_clientes?.length > 0 ? (
              <div className="space-y-2">
                {d.top_clientes.map((c, i) => (
                  <div key={`cli-${c.nombre}`} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate max-w-[60%]">{i + 1}. {c.nombre}</span>
                    <span className="font-semibold text-gray-900">{fmt(c.monto)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Sin datos</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Top 5 Proveedores ({d.mes_actual})</CardTitle>
          </CardHeader>
          <CardContent>
            {d.top_proveedores?.length > 0 ? (
              <div className="space-y-2">
                {d.top_proveedores.map((p, i) => (
                  <div key={`prov-${p.nombre}`} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate max-w-[60%]">{i + 1}. {p.nombre}</span>
                    <span className="font-semibold text-gray-900">{fmt(p.monto)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Sin datos</p>}
          </CardContent>
        </Card>
      </div>

      {/* ── Row 6: OCs sin recepcion > 7 dias ── */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <PackageX className="w-4 h-4 text-red-500" />
              OCs sin Recepcion {`>`} 7 dias ({d.ocs_sin_recepcion_total})
              {d.ocs_sin_recepcion_monto > 0 && (
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  Total neto: {fmt(d.ocs_sin_recepcion_monto)}
                </span>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent data-testid="ocs-sin-recepcion-table">
          {d.ocs_sin_recepcion?.length > 0 ? (
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">OC</th>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">Fecha</th>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">Proveedor</th>
                    <th className="text-right p-2 font-medium text-xs text-muted-foreground">Neto</th>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">OT</th>
                    <th className="text-center p-2 font-medium text-xs text-muted-foreground">Dias</th>
                  </tr>
                </thead>
                <tbody>
                  {d.ocs_sin_recepcion.map((oc, i) => (
                    <tr key={`oc-${oc.numero_oc}`} className="border-t hover:bg-gray-50/50">
                      <td className="p-2 font-mono text-xs">{oc.numero_oc}</td>
                      <td className="p-2 text-xs">{oc.fecha}</td>
                      <td className="p-2 text-xs truncate max-w-[200px]">{oc.proveedor}</td>
                      <td className="p-2 text-xs text-right font-medium">{fmt(oc.neto)}</td>
                      <td className="p-2 text-xs">{oc.ot || <Minus className="w-3 h-3 text-gray-300" />}</td>
                      <td className="p-2 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${
                          oc.dias_sin_recepcion > 30 ? 'bg-red-100 text-red-700' :
                          oc.dias_sin_recepcion > 14 ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {oc.dias_sin_recepcion}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Todas las OCs tienen recepcion al dia</p>
          )}
        </CardContent>
      </Card>

      {/* ── Row 6b: OCs pendientes de VB ── */}
      {d.ocs_sin_vb_lista?.length > 0 && (
        <Card className="border-green-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                OCs Pendientes de VB ({d.ocs_sin_vb})
                {d.ocs_sin_vb_monto > 0 && (
                  <span className="text-xs font-normal text-muted-foreground ml-2">
                    Total: {fmt(d.ocs_sin_vb_monto)}
                  </span>
                )}
              </CardTitle>
              <Link to="/compras/ordenes-compra" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5">
                Ver Listado <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent data-testid="ocs-sin-vb-table">
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">OC</th>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">Fecha</th>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">Proveedor</th>
                    <th className="text-right p-2 font-medium text-xs text-muted-foreground">Total</th>
                    <th className="text-left p-2 font-medium text-xs text-muted-foreground">Solicitante</th>
                    <th className="text-center p-2 font-medium text-xs text-muted-foreground">Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {d.ocs_sin_vb_lista.map((oc) => (
                    <tr key={`vb-${oc.numero_oc}`} className="border-t hover:bg-gray-50/50">
                      <td className="p-2 font-mono text-xs">{oc.numero_oc}</td>
                      <td className="p-2 text-xs">{oc.fecha}</td>
                      <td className="p-2 text-xs truncate max-w-[200px]">{oc.proveedor || oc.rut_proveedor}</td>
                      <td className="p-2 text-xs text-right font-medium">{fmt(oc.total)}</td>
                      <td className="p-2 text-xs">{oc.solicitante || <Minus className="w-3 h-3 text-gray-300 inline" />}</td>
                      <td className="p-2 text-center">
                        <button
                          onClick={async () => {
                            try {
                              await api.put(`/compras/ordenes-compra/${oc.numero_oc}/aprobar`);
                              toast.success(`OC ${oc.numero_oc} aprobada`);
                              if (onRefresh) onRefresh();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Error al aprobar');
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
                          data-testid={`vb-oc-${oc.numero_oc}`}
                        >
                          <ShieldCheck className="w-3 h-3" /> Dar VB
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Row 7: SII Urgentes detalle ── */}
      {d.urgentes_sii_lista?.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-red-700 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Facturas Urgentes SII (prox. a vencer)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {d.urgentes_sii_lista.map((u, i) => (
                <div key={`sii-${u.folio}`} className="flex items-center justify-between text-sm bg-red-50 rounded-lg px-3 py-2">
                  <div>
                    <span className="font-mono font-bold">F-{u.folio}</span>
                    <span className="text-muted-foreground ml-2">{u.proveedor}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{fmt(u.total)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white ${
                      u.dias_restantes <= 1 ? 'bg-red-600 animate-pulse' : u.dias_restantes <= 2 ? 'bg-red-500' : 'bg-orange-500'
                    }`}>
                      {u.dias_restantes}d
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Row 8: Alerta temprana OCs aprobadas sin factura ── */}
      <OCSinFacturaAlertWidget testId="oc-sin-factura-widget-gerencia" />
    </div>
  );
}
